import useUser from "~/lib/useUser";
import { env } from "~/env.mjs";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { type RawUser } from "~/pages/api/auth/user";
import { useRouter } from "next/router";
import { chunk, getCurrentWeekNumber } from "~/utils";

export interface ILeaderboardItem {
  position: number;
  value: number;
  displayName: string;
  isCurrentUser: boolean;
  user?: RawUser;
  playfabId?: string;
}

export interface PaginatedLeaderboard {
  data: ILeaderboardItem[][];
  totalPages: number;
}

export interface PaginatedLeaderboardWeekly {
  data: ILeaderboardItem[];
  totalPages: number;
}

export const useLeaderboard = () => {
  const { query } = useRouter();
  const currentWeekNumber = getCurrentWeekNumber();
  const weekNo = (query.week as string) || currentWeekNumber.toString();
  const isWeekly = weekNo !== "all-time";
  const { user } = useUser();
  const accessToken = user?.accessToken || "";
  const TITLE_ID = "31CD5";

  const url = isWeekly
    ? `${env.NEXT_PUBLIC_PORTAL_API_URL}/api/v1/leaderboard/${TITLE_ID}/weekly`
    : `${env.NEXT_PUBLIC_PORTAL_API_URL}/api/v1/leaderboard/${TITLE_ID}`;

  const queryFn = async () => {
    const { data } = await axios.get<
      PaginatedLeaderboard | PaginatedLeaderboardWeekly
    >(url, {
      params: {
        item: 0,
        top: isWeekly ? 50 : 200,
        name: "PlasmaScore",
        orderBy: "asc",
        weekNo,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  };

  const queryObj = useQuery({
    queryKey: ["leaderboard", weekNo],
    queryFn,
    enabled: true,
  });

  return {
    ...queryObj,
    data: (isWeekly
      ? {
          totalPages: Math.ceil(
            (queryObj.data as PaginatedLeaderboardWeekly)?.data.length / 10
          ),
          data: chunk<ILeaderboardItem>(
            (queryObj.data as PaginatedLeaderboardWeekly)?.data
              ?.sort((a, b) => b.value - a.value)
              ?.map((item, index) => ({
                ...item,
                position: index + 1,
              })),
            10
          ),
        }
      : queryObj.data) as PaginatedLeaderboard,
  };
};

export interface ILeaderboardUserPosition {
  currentDay: {
    position: number;
    value: number;
  };
  currentWeek: {
    position: number;
    value: number;
  };
  previousWeek: {
    position: number;
    value: number;
  };
  allTime: {
    position: number;
    value: number;
  };
}

export const useUserLeaderboardPosition = () => {
  const { user } = useUser();
  const accessToken = user?.accessToken || "";
  const rfoxId = user?.playfabId || "";
  const TITLE_ID = "31CD5";

  const url = `${env.NEXT_PUBLIC_PORTAL_API_URL}/api/v1/leaderboard/${TITLE_ID}/${rfoxId}/weekly`;

  const queryFn = async () => {
    const { data } = await axios.get<ILeaderboardUserPosition>(url, {
      params: {
        name: "PlasmaScore",
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return data;
  };

  return useQuery({
    queryKey: ["leaderboard", rfoxId],
    queryFn,
    enabled: !!accessToken && !!rfoxId,
  });
};
